import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { Button, Input, List, Avatar, Badge, Drawer, Typography, message as antMessage } from "antd";
import { SendOutlined, UserOutlined, ArrowLeftOutlined, WarningOutlined, SearchOutlined, MessageOutlined } from "@ant-design/icons";
import axiosInstance, { baseURL } from "../configs";
import moment from "moment";

const { Text } = Typography;

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU";

const playNotificationSound = () => {
    try {
        const audio = new Audio("/assets/media/notification.mp3"); // Use custom sound if available
        audio.volume = 0.5;
        audio.play().catch(() => {
            // Fallback: browser may block autoplay
            console.log("Audio autoplay blocked");
        });
    } catch (e) {
        console.log("Sound not available");
    }
};

const ChatWidget = () => {
    const [visible, setVisible] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [socket, setSocket] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [typingUsers, setTypingUsers] = useState({});
    const [onlineUserIds, setOnlineUserIds] = useState(new Set());
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Chat mode selection
    const [showMenu, setShowMenu] = useState(false);
    const [chatMode, setChatMode] = useState(null); // null | 'ai' | 'user'
    const [chatHidden, setChatHidden] = useState(() => localStorage.getItem('chatHidden') === 'true');

    const toggleChatHidden = () => setChatHidden(prev => {
        const next = !prev;
        localStorage.setItem('chatHidden', String(next));
        return next;
    });

    // AI Chat state
    const [aiMessages, setAiMessages] = useState([]);
    const [aiInput, setAiInput] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("Sedang memproses...");
    const loadingIntervalRef = useRef(null);

    const messagesEndRef = useRef(null);
    const aiEndRef = useRef(null);
    const selectedUserRef = useRef(null);
    const visibleRef = useRef(false);
    const typingTimeoutRef = useRef(null);

    useEffect(() => {
        selectedUserRef.current = selectedUser;
    }, [selectedUser]);

    useEffect(() => {
        visibleRef.current = visible;
    }, [visible]);

    // Track scroll position to adjust chat button position
    useEffect(() => {
        const handleScroll = () => {
            // Show scroll-to-top button when scrolled more than 300px
            setShowScrollTop(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial position
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (!userStr) return;

        const user = JSON.parse(userStr);
        setCurrentUser(user);

        const socketURL = baseURL.replace(/\/api$/, '');
        console.log("[Chat] Connecting to Socket.IO:", socketURL);

        const newSocket = io(socketURL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        setSocket(newSocket);

        newSocket.on("connect", () => {
            console.log("[Chat] Socket connected:", newSocket.id);
            newSocket.emit("join", user.id_user);
        });

        newSocket.on("connect_error", (error) => {
            console.error("[Chat] Connection error:", error.message);
        });

        newSocket.on("onlineUsers", (userIds) => {
            console.log("[Chat] Online users:", userIds);
            setOnlineUserIds(new Set(userIds.map(id => String(id))));
        });

        newSocket.on("userStatus", (data) => {
            console.log("[Chat] User status update:", data);
            const strUserId = String(data.userId);
            setOnlineUserIds(prev => {
                const newSet = new Set(prev);
                if (data.status === 'online') {
                    newSet.add(strUserId);
                } else {
                    newSet.delete(strUserId);
                }
                return newSet;
            });
            setUsers(prev => prev.map(u =>
                String(u.id_user) === strUserId ? { ...u, isOnline: data.status === 'online' } : u
            ));
        });

        newSocket.on("receiveMessage", (msg) => {
            console.log("[Chat] Received message:", msg);
            const currentSelected = selectedUserRef.current;
            const isVisible = visibleRef.current;
            const msgSenderId = String(msg.senderId || msg.sender_id);
            const isSystemMessage = msg.isSystem || msgSenderId === "SYSTEM";

            // System warning messages always show in current chat
            if (isSystemMessage) {
                setMessages(prev => [...prev, msg]);
                return;
            }

            if (isVisible && currentSelected && String(currentSelected.id_user) === msgSenderId) {
                setMessages(prev => [...prev, msg]);
            } else {
                setUnreadCount(prev => prev + 1);
                playNotificationSound();
                antMessage.info({
                    content: `Pesan baru dari ${msg.senderName || 'User'}`,
                    duration: 3
                });
            }
        });

        newSocket.on("messageSent", (msg) => {
            console.log("[Chat] Message sent:", msg);
            const currentSelected = selectedUserRef.current;
            const msgReceiverId = String(msg.receiverId || msg.receiver_id);
            if (currentSelected && String(currentSelected.id_user) === msgReceiverId) {
                setMessages(prev => [...prev, msg]);
            }
        });

        newSocket.on("userTyping", (data) => {
            console.log("[Chat] User typing:", data);
            setTypingUsers(prev => ({
                ...prev,
                [String(data.userId)]: data.isTyping
            }));
        });

        return () => {
            console.log("[Chat] Disconnecting socket");
            newSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (visible) {
            fetchUsers();
            setUnreadCount(0);
        }
    }, [visible]);

    useEffect(() => {
        if (selectedUser) {
            fetchMessages(selectedUser.id_user);
        }
    }, [selectedUser]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        aiEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleAiSend = async () => {
        if (!aiInput.trim() || aiLoading) return;
        const userMsg = { role: "user", content: aiInput.trim() };
        const updated = [...aiMessages, userMsg];
        setAiMessages(updated);
        setAiInput("");
        setAiLoading(true);

        // Animasi loading: teks berputar setiap 1.8 detik
        const loadingSteps = [
            "Sedang berpikir... 🤔",
            "Mengecek database... 🗄️",
            "Mencari data yang relevan... 🔍",
            "Menyusun jawaban... ✍️",
            "Sebentar ya Pak/Bu... ⏳",
        ];
        let stepIdx = 0;
        setLoadingText(loadingSteps[0]);
        loadingIntervalRef.current = setInterval(() => {
            stepIdx = (stepIdx + 1) % loadingSteps.length;
            setLoadingText(loadingSteps[stepIdx]);
        }, 1800);

        try {
            const userLevel = Number(currentUser?.id_user_level || currentUser?.id_level || 99);
            const isAdmin = userLevel <= 2;
            const res = await axiosInstance.post("/ai/chat", {
                messages: updated,
                isAdmin,
                username: currentUser?.username || currentUser?.name || "admin",
            });
            const replyContent = res.data.reply;
            const isSaved = !!res.data.saved;
            const intentType = res.data.intent || "general";

            // Update loading text berdasarkan intent yang dideteksi
            const intentLabels = {
                stok_item: "Data stok ditemukan ✅",
                stok_importir: "Data importir ditemukan ✅",
                incoming: "Data incoming ditemukan ✅",
                outgoing: "Data outgoing ditemukan ✅",
                slow_moving: "Data slow-moving ditemukan ✅",
                statistik: "Statistik sistem siap ✅",
            };
            if (intentLabels[intentType]) setLoadingText(intentLabels[intentType]);

            clearInterval(loadingIntervalRef.current);
            setAiMessages(prev => [...prev, {
                role: "assistant",
                content: replyContent,
                saved: isSaved,
            }]);
        } catch {
            clearInterval(loadingIntervalRef.current);
            setAiMessages(prev => [...prev, { role: "assistant", content: "Maaf, Atlantik AI sedang tidak tersedia. Coba lagi sebentar." }]);
        } finally {
            setAiLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await axiosInstance.get("/chat/users");
            const usersWithOnline = res.data.map(u => ({
                ...u,
                isOnline: onlineUserIds.has(String(u.id_user))
            }));
            setUsers(usersWithOnline);
        } catch (err) {
            console.error("[Chat] Error fetching users:", err);
        }
    };

    const fetchMessages = async (otherId) => {
        try {
            const res = await axiosInstance.get(`/chat/messages/${otherId}`);
            setMessages(res.data);
        } catch (err) {
            console.error("[Chat] Error fetching messages:", err);
        }
    };

    const handleTyping = (e) => {
        setNewMessage(e.target.value);

        if (socket && selectedUser) {
            // Send typing indicator
            socket.emit("typing", {
                senderId: currentUser.id_user,
                receiverId: selectedUser.id_user,
                isTyping: true
            });

            // Clear previous timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Stop typing after 2 seconds of no input
            typingTimeoutRef.current = setTimeout(() => {
                socket.emit("typing", {
                    senderId: currentUser.id_user,
                    receiverId: selectedUser.id_user,
                    isTyping: false
                });
            }, 2000);
        }
    };

    const handleSend = () => {
        if (!newMessage.trim() || !selectedUser || !socket) return;

        console.log("[Chat] Sending message to:", selectedUser.id_user);
        socket.emit("sendMessage", {
            senderId: currentUser.id_user,
            receiverId: selectedUser.id_user,
            message: newMessage.trim(),
        });
        setNewMessage("");

        // Stop typing indicator
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        socket.emit("typing", {
            senderId: currentUser.id_user,
            receiverId: selectedUser.id_user,
            isTyping: false
        });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "";
        return moment(timestamp).local().format("HH:mm");
    };

    const isUserTyping = selectedUser && typingUsers[String(selectedUser.id_user)];

    // Calculate last seen text
    const getLastSeenText = (lastActivity) => {
        if (!lastActivity) return "Offline";
        const now = moment();
        const lastSeen = moment(lastActivity);
        const diffMinutes = now.diff(lastSeen, 'minutes');
        const diffHours = now.diff(lastSeen, 'hours');
        const diffDays = now.diff(lastSeen, 'days');

        if (diffMinutes < 5) {
            return "Away"; // Recently active but not currently online
        } else if (diffMinutes < 60) {
            return `Last seen: ${diffMinutes} minute(s) ago`;
        } else if (diffHours < 24) {
            return `Last seen: ${diffHours} hour(s) ago`;
        } else if (diffDays < 7) {
            return `Last seen: ${diffDays} day(s) ago`;
        } else {
            return `Last seen: ${lastSeen.format('DD MMM YYYY')}`;
        }
    };

    // Get status color
    const getStatusColor = (isOnline, lastActivity) => {
        if (isOnline) return '#52c41a'; // Green - Online
        if (!lastActivity) return '#bfbfbf'; // Grey - Offline
        const diffMinutes = moment().diff(moment(lastActivity), 'minutes');
        if (diffMinutes < 5) return '#faad14'; // Orange/Yellow - Away
        return '#bfbfbf'; // Grey - Offline
    };

    // Filter users based on search
    const filteredUsers = users.filter(user => {
        const name = (user.name || user.username || '').toLowerCase();
        return name.includes(searchQuery.toLowerCase());
    });

    const renderUserList = () => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Search Box */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                <Input
                    placeholder="Cari user..."
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                    style={{ borderRadius: 20 }}
                />
            </div>

            <List
                itemLayout="horizontal"
                dataSource={filteredUsers}
                locale={{ emptyText: searchQuery ? "User tidak ditemukan" : "Tidak ada user lain" }}
                style={{ flex: 1, overflowY: 'auto' }}
                renderItem={(user) => {
                    const isOnline = onlineUserIds.has(String(user.id_user));
                    const lastSeenText = isOnline ? "● Online sekarang" : getLastSeenText(user.last_activity);
                    const statusColor = getStatusColor(isOnline, user.last_activity);
                    const isAway = !isOnline && user.last_activity && moment().diff(moment(user.last_activity), 'minutes') < 5;

                    return (
                        <List.Item
                            onClick={() => setSelectedUser({ ...user, isOnline })}
                            style={{
                                cursor: 'pointer',
                                padding: '14px 18px',
                                background: selectedUser?.id_user === user.id_user ? '#e6f7ff' : 'transparent',
                                borderBottom: '1px solid #f0f0f0',
                                transition: 'background 0.2s'
                            }}
                        >
                            <List.Item.Meta
                                avatar={
                                    <Badge
                                        dot
                                        status={isOnline ? "success" : (isAway ? "warning" : "default")}
                                        offset={[-4, 32]}
                                    >
                                        <Avatar
                                            icon={<UserOutlined />}
                                            style={{ backgroundColor: statusColor }}
                                            size={44}
                                        />
                                    </Badge>
                                }
                                title={<Text strong style={{ fontSize: 15, color: '#3F4254' }}>{user.name || user.username}</Text>}
                                description={
                                    <Text
                                        type={isOnline ? "success" : (isAway ? "warning" : "secondary")}
                                        style={{ fontSize: 12, color: isAway ? '#faad14' : undefined }}
                                    >
                                        {isAway ? "Away" : lastSeenText}
                                    </Text>
                                }
                            />
                        </List.Item>
                    );
                }}
            />
        </div>
    );

    const renderChatWindow = () => {
        const isOnline = onlineUserIds.has(String(selectedUser.id_user));

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <div style={{
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #e8e8e8',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff'
                }}>
                    <Button
                        icon={<ArrowLeftOutlined style={{ color: '#fff' }} />}
                        type="text"
                        onClick={() => setSelectedUser(null)}
                        style={{ marginRight: 12 }}
                    />
                    <Avatar
                        icon={<UserOutlined />}
                        style={{ marginRight: 12, backgroundColor: isOnline ? '#52c41a' : '#bfbfbf' }}
                        size={40}
                    />
                    <div style={{ flex: 1 }}>
                        <Text strong style={{ color: '#fff', fontSize: 15 }}>
                            {selectedUser.name || selectedUser.username}
                        </Text>
                        <br />
                        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
                            {isUserTyping ? "Sedang mengetik..." : (isOnline ? "● Online" : "○ Offline")}
                        </Text>
                    </div>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f0f2f5' }}>
                    {messages.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#999', marginTop: 60, fontSize: 13 }}>
                            💬 Mulai percakapan dengan mengirim pesan!
                        </div>
                    )}
                    {messages.map((msg, index) => {
                        const isMe = String(msg.senderId || msg.sender_id) === String(currentUser.id_user);
                        const isSystem = msg.isSystem || msg.senderId === "SYSTEM" || msg.sender_id === "SYSTEM";

                        // System warning message style - simple red text with icon, multi-line
                        if (isSystem) {
                            return (
                                <div
                                    key={msg.id || index}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginBottom: 12,
                                        marginTop: 8,
                                        padding: '8px 12px',
                                        width: '100%'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                        <WarningOutlined style={{ color: '#ff4d4f', fontSize: 14, marginRight: 6 }} />
                                        <Text style={{ color: '#ff4d4f', fontSize: 12, fontWeight: 600 }}>
                                            Peringatan dari System:
                                        </Text>
                                    </div>
                                    <Text style={{ color: '#ff4d4f', fontSize: 11, textAlign: 'center' }}>
                                        "Kalo ngomong yang sopan!!"
                                    </Text>
                                    <Text style={{ color: '#ff4d4f', fontSize: 11, textAlign: 'center' }}>
                                        "Buruan minta maaf!"
                                    </Text>
                                </div>
                            );
                        }

                        // Regular message
                        return (
                            <div
                                key={msg.id || index}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isMe ? 'flex-end' : 'flex-start',
                                    marginBottom: 12
                                }}
                            >
                                <div style={{
                                    maxWidth: '70%',
                                    padding: '10px 14px',
                                    borderRadius: 18,
                                    background: isMe ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ffffff',
                                    color: isMe ? '#fff' : '#333',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                    borderBottomRightRadius: isMe ? 4 : 18,
                                    borderBottomLeftRadius: isMe ? 18 : 4,
                                    fontSize: 14
                                }}>
                                    {msg.message}
                                </div>
                                <Text type="secondary" style={{ fontSize: 10, marginTop: 4, paddingLeft: 6, paddingRight: 6 }}>
                                    {formatTime(msg.created_at)}
                                </Text>
                            </div>
                        )
                    })}
                    {isUserTyping && (
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                            <div style={{
                                background: '#e4e6eb',
                                padding: '10px 14px',
                                borderRadius: 18,
                                fontSize: 13,
                                color: '#666'
                            }}>
                                <span className="typing-indicator">●●●</span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area - Fixed Layout */}
                <div style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: '#fff',
                    position: 'relative',
                    zIndex: 10
                }}>
                    <Input
                        value={newMessage}
                        onChange={handleTyping}
                        onPressEnter={handleSend}
                        placeholder="Ketik pesan..."
                        style={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            borderRadius: 24,
                            paddingLeft: 18,
                            paddingRight: 18,
                            border: '1px solid #d9d9d9',
                            height: 44
                        }}
                        size="large"
                    />
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<SendOutlined />}
                        onClick={handleSend}
                        disabled={!newMessage.trim()}
                        style={{
                            flexShrink: 0,
                            background: newMessage.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d9d9d9',
                            border: 'none',
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderAiChat = () => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* AI Header */}
            <div style={{
                padding: '14px 16px',
                display: 'flex', alignItems: 'center',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                color: '#fff'
            }}>
                <Button icon={<ArrowLeftOutlined style={{ color: '#fff' }} />} type="text"
                    onClick={() => { setChatMode(null); setAiMessages([]); }}
                    style={{ marginRight: 10 }} />
                <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginRight: 10, fontSize: 18
                }}>🤖</div>
                <div style={{ flex: 1 }}>
                    <Text strong style={{ color: '#fff', fontSize: 15 }}>Atlantik AI</Text><br />
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                        {aiLoading ? '● Sedang mengetik...' : '● Online'}
                    </Text>
                </div>
            </div>

            {/* AI Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f0f2f5' }}>
                {aiMessages.length === 0 && (
                    <div style={{ textAlign: 'center', marginTop: 40 }}>
                        <div style={{ fontSize: 40 }}>🤖</div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Halo! Saya <b>Atlantik AI</b>.<br />Tanya apa saja seputar sistem inventory ALB.
                        </Text>
                    </div>
                )}
                {aiMessages.map((m, i) => (
                    <div key={i} style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
                        marginBottom: 12
                    }}>
                        <div style={{
                            maxWidth: '80%', padding: '10px 14px',
                            borderRadius: 18,
                            background: m.role === 'user'
                                ? 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)'
                                : m.saved ? '#f0fff4' : '#ffffff',
                            color: m.role === 'user' ? '#fff' : '#333',
                            boxShadow: m.saved ? '0 1px 4px rgba(82,196,26,0.3)' : '0 1px 4px rgba(0,0,0,0.1)',
                            border: m.saved ? '1px solid #b7eb8f' : 'none',
                            borderBottomRightRadius: m.role === 'user' ? 4 : 18,
                            borderBottomLeftRadius: m.role === 'user' ? 18 : 4,
                            fontSize: 13, lineHeight: '1.6',
                            whiteSpace: 'pre-wrap',
                        }}>
                            {m.content}
                        </div>
                        {m.saved && (
                            <div style={{ fontSize: 10, color: '#52c41a', marginTop: 3, paddingLeft: 6, display: 'flex', alignItems: 'center', gap: 3 }}>
                                💾 <span>Knowledge tersimpan</span>
                            </div>
                        )}
                    </div>
                ))}
                {aiLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <div style={{
                            width: 30, height: 30, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                        }}>🤖</div>
                        <div style={{
                            background: '#e4e6eb', padding: '10px 16px', borderRadius: 18,
                            fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 8,
                            border: '1px solid #d0d3e0',
                            animation: 'pulse 1.5s ease-in-out infinite'
                        }}>
                            <span style={{
                                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                animation: 'blink 1s ease-in-out infinite'
                            }} />
                            {loadingText}
                        </div>
                    </div>
                )}
                <div ref={aiEndRef} />
            </div>

            {/* AI Input */}
            <div style={{
                padding: '12px 16px', borderTop: '1px solid #e8e8e8',
                display: 'flex', alignItems: 'center', gap: 10, background: '#fff'
            }}>
                <Input
                    value={aiInput}
                    onChange={e => setAiInput(e.target.value)}
                    onPressEnter={handleAiSend}
                    placeholder="Tanya Atlantik AI..."
                    style={{ flex: 1, borderRadius: 24, paddingLeft: 16, height: 42, border: '1px solid #d9d9d9' }}
                />
                <Button type="primary" shape="circle" icon={<SendOutlined />}
                    onClick={handleAiSend}
                    disabled={!aiInput.trim() || aiLoading}
                    style={{
                        width: 42, height: 42, minWidth: 42, flexShrink: 0,
                        background: aiInput.trim() ? 'linear-gradient(135deg, #1a1a2e, #0f3460)' : '#d9d9d9',
                        border: 'none'
                    }}
                />
            </div>
        </div>
    );

    if (!currentUser) return null;

    return (
        <>
            <style>
                {`
                    @keyframes typing {
                        0%, 60%, 100% { opacity: 0.3; }
                        30% { opacity: 1; }
                    }
                    .typing-indicator span { animation: typing 1.4s infinite; }
                    @keyframes menuFadeIn {
                        from { opacity: 0; transform: translateY(10px) scale(0.95); }
                        to   { opacity: 1; transform: translateY(0)  scale(1); }
                    }
                    @keyframes blink {
                        0%, 100% { opacity: 0.3; transform: scale(0.8); }
                        50% { opacity: 1; transform: scale(1.2); }
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.7; }
                    }
                `}
            </style>

            {/* Floating area - video button + popup menu */}
            {!visible && (
                <div style={{
                    position: 'fixed',
                    bottom: 24,
                    right: showScrollTop ? 90 : 24,
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 0,
                    transition: 'right 0.3s ease'
                }}>
                    {/* Titik kecil saat disembunyikan */}
                    {chatHidden ? (
                        <div
                            title="Tampilkan chat"
                            onClick={toggleChatHidden}
                            style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                cursor: 'pointer', opacity: 0.55,
                                transition: 'opacity 0.2s, transform 0.2s',
                                boxShadow: '0 2px 8px rgba(102,126,234,0.5)'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.5)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '0.55'; e.currentTarget.style.transform = 'scale(1)'; }}
                        />
                    ) : (
                        <>
                            {/* Popup menu */}
                            {showMenu && (
                                <div style={{
                                    marginBottom: 8,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    animation: 'menuFadeIn 0.22s ease'
                                }}>
                                    {/* Atlantik AI option */}
                                    <div onClick={() => { setShowMenu(false); setChatMode('ai'); setVisible(true); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
                                            color: '#fff', padding: '10px 16px', borderRadius: 24,
                                            cursor: 'pointer', boxShadow: '0 4px 16px rgba(15,52,96,0.45)',
                                            fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap',
                                            transition: 'transform 0.15s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <span style={{ fontSize: 20 }}>🤖</span> Atlantik AI
                                    </div>
                                    {/* User Chat option */}
                                    <div onClick={() => { setShowMenu(false); setChatMode('user'); setVisible(true); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: '#fff', padding: '10px 16px', borderRadius: 24,
                                            cursor: 'pointer', boxShadow: '0 4px 16px rgba(102,126,234,0.45)',
                                            fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap',
                                            transition: 'transform 0.15s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <span style={{ fontSize: 20 }}>💬</span> Chat dengan User
                                    </div>
                                </div>
                            )}

                            {/* Tombol hide (×) kecil di atas icon */}
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                    onClick={e => { e.stopPropagation(); toggleChatHidden(); }}
                                    title="Sembunyikan"
                                    style={{
                                        position: 'absolute', top: -4, right: -4, zIndex: 10001,
                                        width: 20, height: 20, borderRadius: '50%', border: 'none',
                                        background: 'rgba(0,0,0,0.45)', color: '#fff',
                                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        padding: 0, lineHeight: 1
                                    }}
                                >×</button>

                                {/* Floating chat button */}
                                <Badge count={unreadCount} offset={[-10, 10]} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                                    <div
                                        onClick={() => setShowMenu(prev => !prev)}
                                        style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <img
                                            src="/assets/media/chat-icon.gif"
                                            alt="Chat"
                                            style={{
                                                width: 98,
                                                height: 98,
                                                display: 'block',
                                                pointerEvents: 'none',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                </Badge>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Chat Drawer */}
            <Drawer
                title={
                    chatMode === 'ai' ? null :
                        (!selectedUser ? "💬 Ngomong Langsung Deh..." : null)
                }
                placement="right"
                closable={true}
                onClose={() => {
                    setVisible(false);
                    setSelectedUser(null);
                    setChatMode(null);
                    setShowMenu(false);
                }}
                visible={visible}
                width={Math.min(380, window.innerWidth - 20)}
                bodyStyle={{ padding: 0, height: 'calc(100% - 55px)', display: 'flex', flexDirection: 'column' }}
                headerStyle={{
                    display: (chatMode === 'ai' || selectedUser) ? 'none' : 'flex',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff'
                }}
            >
                {chatMode === 'ai'
                    ? renderAiChat()
                    : (!selectedUser ? renderUserList() : renderChatWindow())
                }
            </Drawer>
        </>
    );
};

export default ChatWidget;
